import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { distinctHslColor } from 'src/app/utils/color.utils';
import { ColorEntry, ShiftConfig } from 'src/app/shared/models/drilling.models';
import { DayGroup, ShiftColumn, generateColumns, generateDayGroups } from '../drill-activity-chart/utils/column-generator';
import { DeliveryActivityItem, DeliveryBlastRow, DeliveryColorEntry } from './models/delivery-activity.models';
import { SelectedShiftColumn } from 'src/app/shared/models/delivery-activity.models';
import { DeliveryBlockCellComponent } from './delivery-block-cell/delivery-block-cell.component';
import { DeliveryChartControlsComponent, DeliveryTimeScale } from './delivery-chart-controls/delivery-chart-controls.component';

@Component({
  selector: 'delivery-activity-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './delivery-activity-chart.component.html',
  styleUrls: ['./delivery-activity-chart.component.scss'],
  imports: [DeliveryBlockCellComponent, DeliveryChartControlsComponent, DecimalPipe],
})
export class DeliveryActivityChartComponent {
  readonly rows = input<DeliveryBlastRow[]>([]);
  readonly loading = input<boolean>(false);
  readonly shiftConfig = input.required<ShiftConfig>();
  readonly startDate = input.required<Date>();
  readonly endDate = input.required<Date>();
  readonly timeScale = input<DeliveryTimeScale>('week');
  readonly colorBy = input<'equipment' | 'operator'>('equipment');
  readonly palette = input<ColorEntry[]>([]);

  readonly rowClick = output<DeliveryBlastRow>();
  readonly deliveryClick = output<DeliveryActivityItem>();
  readonly dateRangeChange = output<{ start: Date; end: Date }>();
  readonly selectedShiftsChange = output<SelectedShiftColumn[]>();
  readonly selectedDatesChange = output<string[]>();

  protected readonly activeColorBy = signal<'equipment' | 'operator'>('equipment');
  protected readonly activeTimeScale = signal<DeliveryTimeScale>('week');

  private readonly displayStart = signal<Date | null>(null);
  private readonly displayEnd = signal<Date | null>(null);
  protected readonly selectedShiftSet = signal<Set<string>>(new Set<string>());

  constructor() {
    effect(() => this.activeColorBy.set(this.colorBy()));
    effect(() => this.activeTimeScale.set(this.timeScale()));
    effect(() => this.displayStart.set(this.startDate()));
    effect(() => this.displayEnd.set(this.endDate()));

    effect(() => {
      const visibleShiftIds = this.shiftColumns().map(col => col.id);
      const currentSelection = untracked(() => this.selectedShiftSet());
      const nextSelection = new Set(
        visibleShiftIds.filter(id => currentSelection.has(id)),
      );

      if (!this.areSameSets(currentSelection, nextSelection)) {
        this.selectedShiftSet.set(nextSelection);
      }

      this.emitShiftSelection(nextSelection);
    });
  }

  protected readonly shiftColumns = computed<ShiftColumn[]>(() =>
    generateColumns(
      this.displayStart() ?? this.startDate(),
      this.displayEnd() ?? this.endDate(),
      this.shiftConfig(),
    ),
  );

  protected readonly dayGroups = computed<DayGroup[]>(() =>
    generateDayGroups(this.shiftColumns(), this.activeTimeScale()),
  );

  protected readonly selectedDates = computed<string[]>(() => {
    const selected = this.selectedShiftSet();
    const uniqueDates = new Set(
      this.shiftColumns()
        .filter(col => selected.has(col.id))
        .map(col => col.date),
    );

    return this.dayGroups()
      .map(group => group.date)
      .filter(date => uniqueDates.has(date));
  });

  protected readonly cellWidthPx = computed<number>(() =>
    this.activeTimeScale() === 'month' ? 32 : 56,
  );

  protected readonly displayMonthLabel = computed<string>(() => {
    const d = this.displayStart() ?? this.startDate();
    return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  });

  protected getDeliveriesForCell(row: DeliveryBlastRow, col: ShiftColumn): DeliveryActivityItem[] {
    return row.deliveries.filter(d => d.date === col.date && d.shiftIndex === col.shiftIndex);
  }

  protected getShiftTotal(col: ShiftColumn): number {
    return this.rows().reduce(
      (sum, row) => sum + row.deliveries
        .filter(d => d.date === col.date && d.shiftIndex === col.shiftIndex)
        .reduce((total, d) => total + d.loadedKg, 0),
      0,
    );
  }

  protected getRowLoaded(row: DeliveryBlastRow): number {
    return row.deliveries.reduce((sum, d) => sum + d.loadedKg, 0);
  }

  protected getRowHolesLoaded(row: DeliveryBlastRow): number {
    return row.deliveries.reduce((sum, d) => sum + (d.holeCount ?? 0), 0);
  }

  protected getRowHoleProgress(row: DeliveryBlastRow): number {
    if (!row.blastHoleCount) {
      return 0;
    }
    return (this.getRowHolesLoaded(row) / row.blastHoleCount) * 100;
  }

  protected getRowProgress(row: DeliveryBlastRow): number {
    if (!row.targetKg) {
      return 0;
    }

    return (this.getRowLoaded(row) / row.targetKg) * 100;
  }

  protected clamp100(value: number): number {
    return Math.min(Math.max(value, 0), 100);
  }

  protected readonly grandTotal = computed<number>(() =>
    this.rows().reduce((sum, row) => sum + this.getRowLoaded(row), 0),
  );

  /**
   * Colors not explicitly overridden via `palette` are spaced evenly around the hue
   * wheel so they stay distinguishable regardless of how many entries are shown.
   */
  protected readonly legend = computed<DeliveryColorEntry[]>(() => {
    const colorBy = this.activeColorBy();
    const seen = new Map<string, DeliveryColorEntry>();

    for (const row of this.rows()) {
      for (const delivery of row.deliveries) {
        const id = colorBy === 'equipment' ? delivery.equipmentId : delivery.operatorId;
        const label = colorBy === 'equipment' ? delivery.equipmentLabel : delivery.operatorLabel;
        const key = label.toLowerCase();
        if (!label || seen.has(key)) {
          continue;
        }

        const paletteColor = this.palette().find(entry => entry.id === id)?.color;
        seen.set(key, { id, label, color: paletteColor ?? '' });
      }
    }

    const entries = [...seen.values()].sort((a, b) => a.label.localeCompare(b.label));
    entries.forEach((entry, index) => {
      if (!entry.color) { entry.color = distinctHslColor(index, entries.length); }
    });
    return entries;
  });

  protected onColorByChange(value: 'equipment' | 'operator'): void {
    this.activeColorBy.set(value);
  }

  protected toggleShiftSelection(column: ShiftColumn): void {
    const current = this.selectedShiftSet();
    const next = new Set(current);
    const key = column.id;

    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }

    this.selectedShiftSet.set(next);
    this.emitShiftSelection(next);
  }

  protected isShiftSelected(column: ShiftColumn): boolean {
    return this.selectedShiftSet().has(column.id);
  }

  protected isDaySelected(date: string): boolean {
    const selected = this.selectedShiftSet();
    return this.shiftColumns().some(col => col.date === date && selected.has(col.id));
  }

  protected onTimeScaleChange(scale: DeliveryTimeScale): void {
    this.activeTimeScale.set(scale);
    const anchor = this.displayStart() ?? this.startDate();
    const range = this.computeDateRange(scale, anchor);
    this.displayStart.set(range.start);
    this.displayEnd.set(range.end);
    this.dateRangeChange.emit(range);

    if (scale === 'month') {
      setTimeout(() => this.scrollToToday());
    }
  }

  protected onNavigate(direction: -1 | 1): void {
    const start = this.displayStart() ?? this.startDate();
    const end = this.displayEnd() ?? this.endDate();
    let newStart: Date;
    let newEnd: Date;

    if (this.activeTimeScale() === 'week') {
      newStart = new Date(start);
      newStart.setDate(newStart.getDate() + direction * 7);
      newEnd = new Date(end);
      newEnd.setDate(newEnd.getDate() + direction * 7);
    } else {
      newStart = new Date(start.getFullYear(), start.getMonth() + direction, 1);
      newEnd = new Date(newStart.getFullYear(), newStart.getMonth() + 1, 0);
    }

    this.displayStart.set(newStart);
    this.displayEnd.set(newEnd);
    this.dateRangeChange.emit({ start: newStart, end: newEnd });
  }

  protected onDatePick(date: Date): void {
    const range = this.computeDateRange(this.activeTimeScale(), date);
    this.displayStart.set(range.start);
    this.displayEnd.set(range.end);
    this.dateRangeChange.emit(range);
  }

  protected onToday(): void {
    const today = new Date();
    const range = this.computeDateRange(this.activeTimeScale(), today);
    this.displayStart.set(range.start);
    this.displayEnd.set(range.end);
    this.dateRangeChange.emit(range);

    if (this.activeTimeScale() === 'month') {
      setTimeout(() => this.scrollToToday());
    }
  }

  private readonly el = inject(ElementRef);

  private emitShiftSelection(selectedSet: Set<string>): void {
    const selectedShifts = this.shiftColumns()
      .filter(col => selectedSet.has(col.id))
      .map(col => {
        const startHour = this.shiftConfig().shiftStartHours?.[col.shiftIndex] ?? 0;
        const duration = this.shiftConfig().shiftDurationHours;
        const endHour = (startHour + duration) % 24;

        return {
          id: col.id,
          date: col.date,
          shiftIndex: col.shiftIndex,
          shiftLabel: col.shiftLabel,
          startHour,
          endHour,
          crossesMidnight: startHour + duration >= 24,
        } as SelectedShiftColumn;
      });

    this.selectedShiftsChange.emit(selectedShifts);

    const orderedDates = this.dayGroups()
      .map(group => group.date)
      .filter(date => selectedShifts.some(shift => shift.date === date));

    this.selectedDatesChange.emit(orderedDates);
  }

  private areSameSets(a: Set<string>, b: Set<string>): boolean {
    if (a.size !== b.size) {
      return false;
    }

    for (const value of a) {
      if (!b.has(value)) {
        return false;
      }
    }

    return true;
  }

  private scrollToToday(): void {
    const host = this.el.nativeElement as HTMLElement;
    const wrapper = host.querySelector('.dlc-scroll-wrapper') as HTMLElement | null;
    if (!wrapper) {
      return;
    }

    const todayCell = wrapper.querySelector('tbody td.is-today') as HTMLElement | null;
    if (!todayCell) {
      return;
    }

    const sidebarWidth = parseFloat(getComputedStyle(host).getPropertyValue('--sidebar-width')) || 150;
    const visibleWidth = wrapper.clientWidth - sidebarWidth;
    const scrollLeft = todayCell.offsetLeft - sidebarWidth - visibleWidth / 2 + todayCell.offsetWidth / 2;

    wrapper.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  }

  private computeDateRange(scale: DeliveryTimeScale, anchor: Date): { start: Date; end: Date } {
    const y = anchor.getFullYear();
    const mo = anchor.getMonth();
    const d = anchor.getDate();
    const dow = anchor.getDay();

    switch (scale) {
      case 'week': {
        const offset = dow === 0 ? -6 : 1 - dow;
        return {
          start: new Date(y, mo, d + offset),
          end: new Date(y, mo, d + offset + 6),
        };
      }
      case 'month':
        return { start: new Date(y, mo, 1), end: new Date(y, mo + 1, 0) };
    }
  }
}
